"use client";

import { useRef } from "react";
import type { CSSProperties, PointerEvent, ReactElement } from "react";
import { Section } from "../primitives";
import { SectionHeader } from "../shared/SectionHeader";
import { LOOP_COPIES, useLoopTrack } from "../shared/useLoopTrack";
import styles from "./SiteShowcase.module.css";
import type { SiteShowcaseItem, SiteShowcaseProps } from "./SiteShowcase.types";

/**
 * LES SITES QU'ON A FAITS. Deux variantes, un seul modèle de contenu.
 *
 * `grid` (défaut) : une grille de grandes cartes grises, et dans chaque carte
 * la maquette du HAUT du site. Parti pris arbitré par Pierre le 24/09/2026 :
 * « on va faire simple, ça sera juste des cards, et dans la grosse card on
 * verra le début du site en maquette ».
 *
 * `marquee` : la piste horizontale de `CardMarquee`, qui déborde des deux côtés
 * et boucle sans fin, tirée au doigt, au pavé tactile, à la souris ou aux
 * flèches. Demandée le même jour : « les sites à l'horizontale, les cartes
 * commencent à l'extérieur du cadre à droite et sortent à gauche ». Ici la
 * maquette est une capture PLEINE PAGE, et au survol **la page défile dans la
 * carte** : on voit le site se dérouler sans le quitter. Au clic, la carte mène
 * au cas client.
 *
 * Pourquoi le haut du site en `grid` : une capture pleine page réduite à la
 * taille d'une carte ne se lit pas, on n'y voit qu'une bande de couleurs. Le
 * haut de page porte le logo, le titre et l'image du cabinet, c'est-à-dire tout
 * ce qui permet de juger un site en une seconde. En `marquee`, le défilement
 * résout le problème autrement : la page entière passe, mais dans le temps.
 *
 * Pas de fausse fenêtre de navigateur, pas de barre d'adresse, pas de reflet :
 * la carte grise de la charte et la maquette suffisent. C'est la règle du
 * minimum, et l'habillage de navigateur est exactement le genre d'ornement que
 * Pierre écarte.
 *
 * À ne pas confondre avec `ClientShowcase`, qui est un mur de photos de projets
 * avec compteur et témoignages : celui-ci ne montre que des sites.
 */
export function SiteShowcase({
  items,
  variant = "grid",
  columns = 2,
  badge,
  title,
  titleAs = "h2",
  intro,
  cta,
  tone = "white",
  id,
}: SiteShowcaseProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const loops = useRef(new Map<HTMLElement, Animation[]>());
  const shown = items.slice(0, 8);
  useLoopTrack(trackRef, variant === "marquee" ? shown.length : 0);

  /* LE SURVOL EN BOUCLE (variante `marquee`, 14/10/2026, voulu par Pierre).
     Tant que la souris reste sur la carte : la page recule sur le fond flou,
     défile jusqu'à `scrollStop` (fin d'une des premières sections, pas le bas
     de la page), marque un temps, remonte, revient au premier plan, souffle,
     et recommence. Web Animations API plutôt que @keyframes : au départ de la
     souris on repart de la position EXACTE où en est l'animation, sans saut.
     Pointeurs fins seulement, rien en mouvement réduit. */
  const startLoop = (card: HTMLElement, stop?: number) => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const frame = card.querySelector<HTMLElement>(`.${styles.frame}`);
    const stage = card.querySelector<HTMLElement>(`.${styles.stage}`);
    const shot = card.querySelector<HTMLElement>(`.${styles.shot}`);
    const backdrop = card.querySelector<HTMLElement>(`.${styles.backdrop}`);
    if (!frame || !stage || !shot || !backdrop || !shot.clientHeight) return;
    stopLoop(card, false);
    const scale = shot.clientWidth / 1440;
    const dist = Math.max(0, Math.min((stop ?? 2700) * scale, shot.clientHeight - frame.clientHeight));
    const down = Math.min(9000, Math.max(3000, (dist / 140) * 1000));
    /* Départ vif (retour de Pierre, 14/10) : le recul dure 350 ms et le
       défilement démarre 120 ms après l'entrée de la souris, pendant le recul,
       sur une courbe qui part vite et freine en arrivant. */
    const recoilIn = 350, lead = 120, recoilOut = 600, hold = 900, up = down * 0.7, rest = 700;
    const total = lead + down + hold + up + recoilOut + rest;
    const at = (ms: number) => ms / total;
    const out = "cubic-bezier(0.23, 1, 0.32, 1)";
    const glide = "cubic-bezier(0.3, 0.2, 0.2, 1)";
    const inOut = "cubic-bezier(0.45, 0, 0.55, 1)";
    const back = "translateY(5%) scale(0.9)";
    const t1 = at(recoilIn), t2 = at(lead + down), t3 = at(lead + down + hold), t4 = at(lead + down + hold + up), t5 = at(lead + down + hold + up + recoilOut);
    const opts = { duration: total, iterations: Infinity };
    const anims = [
      stage.animate([{ offset: 0, transform: "none", easing: out }, { offset: t1, transform: back }, { offset: t4, transform: back, easing: out }, { offset: t5, transform: "none" }, { offset: 1, transform: "none" }], opts),
      shot.animate([{ offset: 0, transform: "translateY(0)" }, { offset: at(lead), transform: "translateY(0)", easing: glide }, { offset: t2, transform: `translateY(${-dist}px)` }, { offset: t3, transform: `translateY(${-dist}px)`, easing: inOut }, { offset: t4, transform: "translateY(0)" }, { offset: 1, transform: "translateY(0)" }], opts),
      backdrop.animate([{ offset: 0, opacity: 0, easing: "ease-out" }, { offset: t1, opacity: 1 }, { offset: t4, opacity: 1, easing: "ease" }, { offset: t5, opacity: 0 }, { offset: 1, opacity: 0 }], opts),
    ];
    loops.current.set(card, anims);
  };

  /* Départ de la souris : on fige l'état courant puis on revient au repos en
     500 ms. Lent quand on regarde, rapide quand on s'en va. */
  const stopLoop = (card: HTMLElement, settle = true) => {
    const anims = loops.current.get(card);
    if (!anims) return;
    loops.current.delete(card);
    for (const anim of anims) {
      const el = (anim.effect as KeyframeEffect | null)?.target as HTMLElement | null;
      if (!el) continue;
      const cs = getComputedStyle(el);
      const from = { transform: cs.transform === "none" ? "none" : cs.transform, opacity: cs.opacity };
      anim.cancel();
      if (settle) el.animate([from, { transform: "none", opacity: el.classList.contains(styles.backdrop) ? "0" : "1" }], { duration: 500, easing: "cubic-bezier(0.23, 1, 0.32, 1)" });
    }
  };

  const renderCard = (item: SiteShowcaseItem, index: number): ReactElement => {
    const Inner = item.href ? "a" : "div";
    return (
      <li
        key={index}
        className={styles.card}
        {...(variant === "marquee"
          ? { onPointerEnter: (event: PointerEvent<HTMLLIElement>) => startLoop(event.currentTarget, item.scrollStop), onPointerLeave: (event: PointerEvent<HTMLLIElement>) => stopLoop(event.currentTarget) }
          : {})}
      >
        <Inner className={styles.inner} {...(item.href ? { href: item.href } : {})}>
          <span className={styles.frame} style={{ "--shot-url": `url("${item.image.src}")` } as CSSProperties}>
            {/* Fond flou de la même capture (variante `marquee`) : il apparaît
                quand la page recule au survol et donne l'ambiance de couleur. */}
            <span className={styles.backdrop} aria-hidden="true" />
            <span className={styles.stage}>
              {/* Classe doublée : normalize pose `img { block-size: auto }`, la
                  propriété logique équivalente à `height`, qui gagne sur une
                  classe simple. Même piège que sur TeamCards. */}
              <img className={styles.shot} src={item.image.src} alt={item.image.alt} loading="lazy" />
            </span>
          </span>
          <span className={styles.name}>{item.name}</span>
          {item.meta ? <span className={styles.meta}>{item.meta}</span> : null}
        </Inner>
      </li>
    );
  };

  if (variant === "marquee") {
    const arrows = (
      <div className={styles.nav}>
        <button type="button" className={styles.navButton} data-marquee-prev="true" aria-label="Sites précédents">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <button type="button" className={styles.navButton} data-marquee-next="true" aria-label="Sites suivants">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
    return (
      <Section tone={tone} id={id} bleed className={styles.section} data-variant="marquee">
        <div className={styles.head}>
          <SectionHeader badge={badge} title={title} titleAs={titleAs} intro={intro} cta={cta} after={arrows} />
        </div>
        <div ref={trackRef} className={styles.track} data-marquee-track="true">
          {Array.from({ length: LOOP_COPIES }, (_, copy) => (
            <ul key={copy} className={styles.set} data-set={copy} aria-hidden={copy === 1 ? undefined : "true"}>
              {shown.map(renderCard)}
            </ul>
          ))}
        </div>
      </Section>
    );
  }

  return (
    <Section tone={tone} id={id}>
      <SectionHeader badge={badge} title={title} titleAs={titleAs} intro={intro} cta={cta} />
      <ul className={styles.grid} data-columns={columns}>
        {items.map(renderCard)}
      </ul>
    </Section>
  );
}
